import { accentColors } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export const StyledSkeleton = styled.div<{ width?: string; height?: string }>`
  position: relative;
  overflow: hidden;
  background: rgba(28, 37, 56, 0.8);
  border-radius: 4px;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '100%'};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, rgba(28, 37, 56, 0), rgba(34, 41, 63, 0.5), rgba(28, 37, 56, 0));
    transform: translateX(-100%);
    animation: ${shimmer} 1.2s infinite;
  }
`;

export const StyledSectionWrapper = styled.section`
  background-color: ${({ theme }) => theme.colors.black};
  overflow: hidden;
  min-height: 100vh;
  @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
    height: 100vh;
  }
  > div {
    height: 100%;
    display: flex;
    flex-direction: column;
    @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
      max-width: 134rem;
    }
    @media screen and (min-width: ${({ theme }) => theme.sizes.xxl}) {
      max-width: 140rem;
    }
  }
`;

export const StyledLogo = styled.div`
  padding-top: 46px;
  position: relative;
  z-index: 2;
  img {
    width: 66px;
    height: 38px;
  }
  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    img {
      width: 140px;
      height: 80px;
    }
  }
`;

export const StyledPersonalBadgesContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  text-align: center;
  --header-h: -128.5px;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    position: relative;
    margin-top: -80px;
    gap: 120px;
  }
  @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
    text-align: left;
    margin-top: var(--header-h);
    flex-direction: row;
    gap: 120px;
  }
  @media screen and (min-width: ${({ theme }) => theme.sizes.xxl}) {
    gap: 209px;
  }
  align-items: center;
  color: ${({ theme }) => theme.colors.white};
  .left-side {
    @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
      margin-top: 180px;
    }
    header {
      margin-bottom: 16px;
      font-size: ${({ theme }) => theme.fonts.sizes.medium};
    }
    h1 {
      color: ${({ theme }) => theme.colors.grey[100]};
      font-family: ${({ theme }) => theme.fonts.familyUbuntu};
      font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
      font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
      @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
        font-size: 54px;
      }
    }
    h2 {
      color: ${({ theme }) => theme.colors.grey[200]};
      font-weight: ${({ theme }) => theme.fonts.weight.regular};
      font-size: ${({ theme }) => theme.fonts.sizes.medium};
      @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
        font-size: 24px;
      }
    }
    .username {
      color: ${({ theme }) => theme.colors.primary.main};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    .username + span {
      color: ${({ theme }) => theme.colors.grey[200]};
    }
    .cards {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      margin-top: 46px;
      @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
        flex-wrap: nowrap;
      }
      justify-content: center;
    }
    .cards > .card {
      min-width: 156px;
      @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
        min-width: 206px;
      }
      display: flex;
      text-align: left;

      flex-direction: column;
      gap: 8px;
      padding: 16px 24px;
      border-radius: ${({ theme }) => theme.radius.small};
      background: ${({ theme }) => theme.colors.secondary.main};
      .title {
        color: ${({ theme }) => theme.colors.grey[200]};
        font-size: 20px;
        font-weight: ${({ theme }) => theme.fonts.weight.regular};
      }
      .content {
        display: flex;
        align-items: center;
        gap: 10px;
        & > :last-child {
          color: ${({ theme }) => theme.colors.grey[100]};
          font-family: ${({ theme }) => theme.fonts.familyUbuntu};
          font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
          font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
        }
      }
    }
    footer {
      font-size: ${({ theme }) => theme.fonts.sizes.medium};
      margin-top: 60px;
      margin-bottom: 60px;
      max-width: 65%;
      margin-left: auto;
      margin-right: auto;
      @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
        margin-top: 203px;
        max-width: initial;
        margin-left: initial;
        margin-right: initial;
      }
      @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
        margin-top: 203px;
        margin-bottom: 0px;
      }
      @media screen and (min-width: ${({ theme }) => theme.sizes.xxl}) {
        margin-top: 293px;
      }
      & > :last-child {
        color: ${accentColors.info.dark};
        cursor: pointer;
        text-decoration: none;
        transition: all 0.3s;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

export const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 28px;
`;

export const StyledHexagonContainer = styled.div`
  position: relative;
  top: -90px;
  margin-top: 20px;
  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    top: 0px;
    margin-top: 0px;
  }
`;

export const StyledHexagon = styled.div`
  --w: 241px;
  --h: 282px;

  &:first-child,
  &:last-child {
    display: none;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    --w: 338px;
    --h: 390px;
    &:first-child,
    &:last-child {
      display: flex;
    }
  }
  @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
    --w: 541px;
    --h: 624px;
  }

  width: var(--w); /* Scaled width */
  height: calc(var(--w) * 60 / 104); /* Scaled height using the original ratio */
  background-color: ${({ theme }) => theme.colors.secondary.main};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  &:first-child,
  &:last-child {
    position: absolute;
    top: calc(var(--w) * -93 / 104);
  }

  &:first-child {
    left: calc(var(--w) * 75 / 104);
  }

  &:last-child {
    left: calc(var(--w) * -75 / 104);
  }
  @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
    &:first-child,
    &:last-child {
      left: calc(var(--w) * 65 / 104);
    }

    &:first-child {
      top: calc(var(--w) * 105 / 104);
    }

    &:last-child {
      top: calc(var(--w) * -105 / 104);
    }
  }

  img {
    position: absolute;
    z-index: 1;
    width: 155px;
    height: 183px;
    @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
      width: 213px;
      height: 251px;
    }

    @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
      width: 340px;
      height: 400px;
    }
  }

  &:before,
  &:after {
    content: '';
    width: 0;
    border-left: calc(var(--w) * 52 / 104) solid transparent;
    border-right: calc(var(--w) * 52 / 104) solid transparent;
    position: absolute;
  }

  &:before {
    border-bottom: calc(var(--w) * 30 / 104) solid ${({ theme }) => theme.colors.secondary.main};
    top: calc(var(--w) * -30 / 104);
  }

  &:after {
    border-top: calc(var(--w) * 30 / 104) solid ${({ theme }) => theme.colors.secondary.main};
    bottom: calc(var(--w) * -30 / 104);
  }
`;
